import { omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyAssignmentRollover, ROLLOVER_GROUPS } from 'utils/assignmentRollover';

const useAssignmentSettingsImport = ({
    form,
    isFeedbackFeatureDisabled,
    isGradingFeatureDisabled,
    isInlineCommentsFeatureDisabled,
    isRubricFeaturedDisabled,
    setErrors,
    setForm,
}) => {
    const { t } = useTranslation('assignment');
    const [dialog, setDialog] = useState({ open: false });
    const [forceRteRevision, setForceRteRevision] = useState(0);
    const [settingsRevision, setSettingsRevision] = useState(0);
    const rolloverDisabledGroups = useMemo(
        () =>
            [
                isRubricFeaturedDisabled && ROLLOVER_GROUPS.RUBRIC,
                isGradingFeatureDisabled && ROLLOVER_GROUPS.GRADING,
                isFeedbackFeatureDisabled && ROLLOVER_GROUPS.FEEDBACK,
                isFeedbackFeatureDisabled && ROLLOVER_GROUPS.FORMATIVE,
                isInlineCommentsFeatureDisabled && ROLLOVER_GROUPS.INLINE_COMMENTS,
            ].filter(Boolean),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled, isInlineCommentsFeatureDisabled, isRubricFeaturedDisabled]
    );
    const onSettingsReplaced = useCallback(() => setSettingsRevision((value) => value + 1), []);
    const bumpForceRteRevision = useCallback(() => setForceRteRevision((value) => value + 1), []);
    const openImportDialog = useCallback(() => setDialog({ open: true }), []);
    const closeImportDialog = useCallback(() => setDialog({ open: false }), []);
    const importAssignmentSettings = useCallback(
        ({ gradingMapping, selectedGroups, source }) => {
            if (selectedGroups.includes(ROLLOVER_GROUPS.GRADING)) onSettingsReplaced();
            setForm((current) =>
                applyAssignmentRollover({
                    targetForm: current,
                    sourceAssignment: source,
                    selectedGroups,
                    disabledGroups: rolloverDisabledGroups,
                    gradingMapping,
                })
            );
            setErrors((current) => {
                const resolved = omit(
                    current,
                    [
                        selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION) && 'description',
                        selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) && 'rubric',
                        selectedGroups.includes(ROLLOVER_GROUPS.GRADING) && 'gradingSettings',
                        selectedGroups.includes(ROLLOVER_GROUPS.FEEDBACK) && 'feedbackSettings',
                        selectedGroups.includes(ROLLOVER_GROUPS.FORMATIVE) && 'studentFeedbackSettings',
                    ].filter(Boolean)
                );
                if (selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION) && !`${source?.description ?? ''}`.trim()) {
                    resolved.description = true;
                }
                if (selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) && !source?.rubric) resolved.rubric = true;
                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.GRADING) &&
                    form?.gradingSettings
                ) {
                    resolved.gradingSettings = `(${t('needs-update', 'needs update')})`;
                }
                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.FEEDBACK) &&
                    form?.feedbackSettings?.structure === 'templated'
                ) {
                    resolved.feedbackSettings = `(${t('needs-update', 'needs update')})`;
                }
                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.FORMATIVE) &&
                    form?.studentFeedbackSettings?.structure === 'templated'
                ) {
                    resolved.studentFeedbackSettings = `(${t('needs-update', 'needs update')})`;
                }
                return resolved;
            });
            if (selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION)) bumpForceRteRevision();
            closeImportDialog();
        },
        [
            bumpForceRteRevision,
            closeImportDialog,
            form,
            onSettingsReplaced,
            rolloverDisabledGroups,
            setErrors,
            setForm,
            t,
        ]
    );

    return {
        closeImportDialog,
        bumpForceRteRevision,
        dialog,
        forceRteRevision,
        importAssignmentSettings,
        onSettingsReplaced,
        openImportDialog,
        rolloverDisabledGroups,
        settingsRevision,
    };
};

export default useAssignmentSettingsImport;
